import type {
  AuthMeResponse,
  JwtTokens,
  TokenClaims,
  Workspace,
  WorkspaceRole,
} from "@/definitions/types";
import { CanceledError } from "axios";
import { jwtDecode } from "jwt-decode";
import { defineStore } from "pinia";
import { computed, ref } from "vue";
import {
  applyWorkspaceSwitch,
  getNewToken,
  getNewTokenQuietly,
  getTokenForWorkspace,
  goLoginPage,
  LOGIN_REASON_REMOVED,
  signOut,
  workspaceRemovedMessage,
} from "@/utils/commands";
import { logger } from "@/utils/logger";
import {
  clearSentryUser,
  setSentryUser,
  setSentryWorkspace,
} from "@/utils/sentry";
import {
  assertTabSession,
  clearSessionNotice,
  isTabSessionIntact,
} from "@/utils/session";
import { tokenStorage } from "@/utils/storage";
import { toastWarning } from "@/utils/toaster";

const initialTokens = (): JwtTokens => ({
  accessToken: "",
  refreshToken: "",
});

const initialInfo = (): TokenClaims => ({
  id: 0,
  loginId: "",
  name: "",
  type: "",
  managerFlag: false,
  authorities: [],
  workspaceId: null,
  role: null,
});

const initialWorkspace = (): Workspace => ({
  id: 0,
  name: "",
  role: "",
  plan: "",
});

/**
 * `applyMe` 의 역할 동기화 재발급이 진행 중인가(모듈 스코프 — 스토어는 싱글턴이라 동시에 여러 번
 * 들어와도 한 번만 부른다, 2차 교차 연계 보완 Y3-06).
 */
let roleSyncInFlight = false;

/**
 * 진행 중인 자동 재발급(4차 교차 연계 보완 리뷰 L9). 같은 순간 만료된 요청 여럿이 한 재발급을 함께 기다린다 —
 * 따로 부르면 리프레시 토큰 교체가 겹치고, 먼저 끝난 쪽이 옮긴 워크스페이스를 뒤에 끝난 쪽이 모른다.
 */
let reissueInFlight: Promise<void> | null = null;

/**
 * 세션 세대. `clearAuth`(로그아웃 · 세션 만료 · 로그인 화면 진입)마다 올린다. 재발급이 시작된 뒤 세션이 끝났으면(세대가 달라졌으면) 늦게 끝난
 * 재발급이 `saveTokens` 로 지워진 세션을 되살리지 않게 저장을 건너뛴다(27차 R27-05).
 */
let sessionGeneration = 0;

/** 역할 높낮이(역할 순위의 단일 출처 — `utils/commands.ts` 의 강등 판정도 이것을 쓴다, 89차 R3). 올라간 역할은 화면이 다시 불러올 때까지 보류한다(`syncRole`). */
export const ROLE_RANK: Record<WorkspaceRole, number> = {
  VIEWER: 1,
  INTERVIEWER: 2,
  HR_ADMIN: 3,
  OWNER: 4,
};

/** 두 역할 중 낮은 쪽. 한쪽이 비어 있으면 다른 쪽. */
function lowerRole(a: WorkspaceRole, b: WorkspaceRole | ""): WorkspaceRole {
  return b && ROLE_RANK[b] < ROLE_RANK[a] ? b : a;
}

/**
 * 인증/세션 스토어
 * - JWT 토큰 쌍, 토큰 클레임, 현재 워크스페이스, 소속 워크스페이스 목록을 보관한다.
 * - 멀티테넌트이므로 화면은 항상 `workspace` 기준으로 표시한다.
 * - `workspaces` 는 로그인 응답과 `GET api/v1/auth/me` 응답 양쪽에서 채운다.
 */
export const useAuthStore = defineStore(
  "auth",
  () => {
    const tokens = ref<JwtTokens>(initialTokens());
    const info = ref<TokenClaims>(initialInfo());
    const workspace = ref<Workspace>(initialWorkspace());
    const workspaces = ref<Workspace[]>([]);
    /**
     * 토큰은 이미 올라갔지만 화면에는 아직 반영하지 않은 역할(없으면 ""). 4차 교차 연계 보완 리뷰 M2 —
     * 화면은 역할이 바뀌면 다시 마운트하는데(`DefaultLayout.vue`), 저장하지 않은 입력이 있어 사용자가 다시
     * 불러오기를 미루면 그 사이 화면의 쓰기 게이트는 **옛(낮은) 역할**을 봐야 한다. 가려서 받은 값(응답자 연락처 ·
     * 처우)을 든 채 쓰기가 열리면 저장이 가려진 빈 값으로 저장값을 덮기 때문이다. 내려간 역할은 보류하지 않는다
     * (쓰기를 곧바로 잠가야 서버 403 과 어긋나지 않는다).
     */
    const pendingRole = ref<WorkspaceRole | "">("");

    const loggedIn = computed(
      () =>
        Boolean(tokens.value.accessToken) &&
        Boolean(info.value.id) &&
        tokens.value.accessToken === tokenStorage.getAccessToken() &&
        tokens.value.refreshToken === tokenStorage.getRefreshToken(),
    );

    const authorities = computed(() => info.value.authorities);

    function clearAuth(): void {
      sessionGeneration += 1;
      tokenStorage.clearTokens();
      tokens.value = initialTokens();
      info.value = initialInfo();
      workspace.value = initialWorkspace();
      workspaces.value = [];
      pendingRole.value = "";
      // 이 탭의 세션이 끝나면 탭 세션 안내도 끝낸다 — 남겨 두면 같은 탭에서 다시 로그인한 직후 요청이 모두 막혔다(30차 수정 라운드 M1).
      clearSessionNotice();
      clearSentryUser();
    }

    /**
     * 현재 워크스페이스를 바꾼다. 보류 역할(`pendingRole`)은 **다른 워크스페이스로 바뀔 때만** 버린다(6차 U5-10) — 같은
     * 워크스페이스의 이름 · 구분(`plan` — 만든 사용자의 회원 유형 기준)만 고치는 호출(`WorkspacePage.vue` 정보 저장)이 보류를 지우면, 올라간 역할이 다음 재발급까지
     * 화면에 반영되지 않고 상단 "역할이 바뀌었습니다" 안내도 사라진다.
     */
    function setWorkspace(newWorkspace: Workspace): void {
      if (newWorkspace.id !== workspace.value.id) pendingRole.value = "";
      workspace.value = { ...newWorkspace };
      // 이름 · 구분(`plan`)을 고쳐도 전환 셀렉트(`workspaces[]`)와 다시 전환할 때 옛 값으로 돌아가지 않게 같은 항목을 맞춘다(24차 F24-03).
      workspaces.value = workspaces.value.map((item) =>
        item.id === newWorkspace.id
          ? { ...item, name: newWorkspace.name, plan: newWorkspace.plan }
          : item,
      );
      setSentryWorkspace(workspace.value);
    }

    /** 지금 워크스페이스의 역할을 화면에 반영한다(`workspace.role` · `workspaces[]` 의 같은 항목). */
    function applyRole(role: WorkspaceRole): void {
      pendingRole.value = "";
      if (role === workspace.value.role) return;
      workspace.value = { ...workspace.value, role };
      workspaces.value = workspaces.value.map((item) =>
        item.id === workspace.value.id ? { ...item, role } : item,
      );
      setSentryWorkspace(workspace.value);
    }

    /**
     * 지금 워크스페이스의 토큰 역할을 화면 역할로 맞춘다. 내려가거나(쓰기를 곧바로 잠금) 처음 정해지면 바로
     * 반영하고, 올라가면 `pendingRole` 에 보류한다 — 화면이 다시 불러올 때 `applyPendingRole` 로 반영한다(M2).
     */
    function syncRole(role: WorkspaceRole): void {
      const current = workspace.value.role;
      if (role === current) {
        pendingRole.value = "";
      } else if (current && ROLE_RANK[role] > ROLE_RANK[current]) {
        pendingRole.value = role;
      } else {
        applyRole(role);
      }
    }

    /**
     * 보류한 올라간 역할을 반영한다(`DefaultLayout.vue` 가 화면을 다시 불러오기 직전 · 워크스페이스 전환 끝에 부른다).
     * **지금 액세스 토큰의 역할과 같을 때만** 반영하고, 다르면 버린다(4차 재리뷰 N1). `pendingRole` 은 영속 상태라
     * 새로 고침 뒤에도 남는데, 그 사이 다른 탭 · 서버에서 다시 강등됐으면 옛 높은 역할이 `/auth/me` 전까지 잠깐 화면
     * 역할이 된다. 평소 경로(`syncRole` 이 토큰 역할로 보류)는 늘 같으므로 동작이 바뀌지 않는다.
     */
    function applyPendingRole(): void {
      const role = pendingRole.value;
      if (!role) return;
      if (role === tokenRole()) {
        applyRole(role);
      } else {
        pendingRole.value = "";
      }
    }

    /** 지금 저장된 액세스 토큰의 역할(디코딩 실패 · 토큰 없음 · Admin 토큰이면 null). */
    function tokenRole(): WorkspaceRole | null {
      const accessToken = tokenStorage.getAccessToken();
      if (!accessToken) return null;
      try {
        return jwtDecode<TokenClaims>(accessToken).role ?? null;
      } catch {
        return null;
      }
    }

    function setWorkspaces(newWorkspaces: Workspace[]): void {
      workspaces.value = [...newWorkspaces];
    }

    /**
     * `GET api/v1/auth/me` 응답을 스토어에 반영한다.
     * - 토큰 클레임에서 온 `info` 중 서버가 알려준 필드만 갱신한다.
     * - `workspaces[]` 를 갱신하고, 서버가 지정한 `workspaceId` 를 현재 워크스페이스로 맞춘다.
     * - `me.role` 은 지금 토큰의 역할, `matched.role` 은 `workspaces[]` 의 DB 역할이다. 구성원 역할이
     *   바뀐 직후에는 둘이 어긋날 수 있어(2차 교차 연계 보완, Y3-06) `getNewTokenQuietly()` 로 한 번만
     *   조용히 재발급해 토큰을 DB 역할로 맞춘다. **`reIssueAccessToken()` 은 여기서 쓰지 않는다** — 그
     *   함수는 재발급이 실패하면 로그인 화면으로 보내는데, 이 시점 토큰은 아직 유효하다(역할만 어긋난
     *   것이지 만료가 아니다). `getNewTokenQuietly` 는 실패해도 로그아웃하지 않고 경고만 남긴다(6차 U5-11 —
     *   예전 이유였던 "액세스 토큰을 먼저 지움" 은 4차 L9 로 없어졌다). 동시에
     *   여러 번 `applyMe` 가 불려도 모듈 스코프 `roleSyncInFlight` 로 한 번만 재발급한다(무한 반복 금지
     *   — 재발급이 끝나면 다음 `applyMe` 호출에서 다시 판정한다).
     * - `matched` 가 없으면(현재 워크스페이스에서 제거됨, 3차 교차 연계 보완 Z3-01) 다른 소속이 있으면
     *   첫 워크스페이스로 전환하고, 없으면 로그인 화면으로 보낸다. 예전에는 아무것도 하지 않아 새로
     *   고쳐도 옛 워크스페이스 · 옛 역할이 그대로 남았다. `roleSyncInFlight` 를 함께 써서 무한 반복을
     *   막는다. 전환 절차(쿼리 비우기 · 진행 중 요청 취소 · 토큰 · 스토어 갱신)는 `DefaultLayout.vue` 의
     *   사용자가 고른 전환과 같은 `applyWorkspaceSwitch`(`utils/commands.ts`)를 쓴다. 전환 요청 자체가
     *   실패해도(네트워크 오류 등) 다른 소속이 있었다는 사실은 바뀌지 않으므로 로그아웃시키지 않고
     *   경고 토스트만 남긴다 — 로그인 화면으로 보내는 것은 다른 소속이 전혀 없을 때뿐이다.
     */
    function applyMe(me: AuthMeResponse): void {
      info.value = {
        ...info.value,
        id: me.id,
        loginId: me.loginId,
        name: me.name,
        workspaceId: me.workspaceId,
        role: me.role,
      };
      setWorkspaces(me.workspaces);
      const matched = me.workspaces.find((item) => item.id === me.workspaceId);
      if (matched) {
        // 화면 역할은 지금 토큰 역할(`me.role`)과 DB 역할(`matched.role`) 중 **낮은 쪽**이다. 토큰 역할이 높으면 곧
        // 아래 조용한 재발급이 DB 역할로 낮추므로 그 사이 쓰기를 먼저 잠그고, DB 역할이 높으면 재발급의 `saveTokens`
        // 가 올린다(4차 재리뷰 N2 — 다른 워크스페이스로 맞출 때 옛 높은 토큰 역할이 잠깐 보이던 틈). 같은 워크스페이스면
        // 지금 화면 역할을 두고 `syncRole` 로만 바꿔, 올라간 역할이 화면을 다시 불러오기 전에 쓰기를 열지 않게 한다(4차 M2).
        setWorkspace(
          matched.id === workspace.value.id
            ? { ...matched, role: workspace.value.role }
            : matched,
        );
        syncRole(lowerRole(me.role, matched.role));
        if (matched.role !== me.role && !roleSyncInFlight) {
          roleSyncInFlight = true;
          void getNewTokenQuietly()
            .then((newTokens) => {
              // 응답을 기다리는 사이 다른 탭이 워크스페이스를 바꿨으면 되돌리지 않는다(30차 수정 라운드 L2).
              if (newTokens && isTabSessionIntact()) saveTokens(newTokens);
            })
            .finally(() => {
              roleSyncInFlight = false;
            });
        }
      } else if (!roleSyncInFlight) {
        const fallback = me.workspaces[0];
        if (!fallback) {
          // 소속이 하나도 남지 않은 제거 — 「만료」 가 아니라 제거 사유로 로그인에 보낸다(90차 C90-03c)
          void goLoginPage(undefined, LOGIN_REASON_REMOVED);
        } else {
          roleSyncInFlight = true;
          void getTokenForWorkspace(fallback.id)
            .then(async (newTokens) => {
              if (
                !newTokens ||
                !(await applyWorkspaceSwitch(fallback, newTokens))
              ) {
                logger.warn("Fallback workspace switch after removal failed");
                toastWarning(
                  "소속이 해제됐지만 다른 워크스페이스로 전환하지 못했습니다. 새로고침하거나 다시 로그인해 주세요.",
                );
                return;
              }
              toastWarning(workspaceRemovedMessage(fallback.name));
            })
            .finally(() => {
              roleSyncInFlight = false;
            });
        }
      }
      setSentryUser(info.value);
    }

    /**
     * 만료된 액세스 토큰을 재발급한다(요청 인터셉터 · `must-renew` 경로).
     * - 진행 중인 재발급이 있으면 그것을 함께 기다린다(리뷰 L9). 예전에는 재발급 전에 액세스 토큰을 먼저 지워서,
     *   같은 순간 나간 다른 요청의 `getValidatedAccessToken` 이 빈 토큰을 보고 세션을 끊었다. 이제 지우지 않는다 —
     *   만료된 토큰을 본 요청은 여기로 와서 같은 재발급을 기다리고, 재발급이 실패하면 `getNewToken` 이 로그인
     *   화면으로 보내며 토큰을 모두 지운다.
     * - 재발급 중 소속 해제로 다른 워크스페이스로 옮겼으면(4차 교차 연계 보완 W5-02, `getNewToken`) 재발급을
     *   부른 요청은 옛 워크스페이스의 요청이다 — 새 토큰으로 보내면 다른 워크스페이스에 쓰게 되므로 함께 기다린
     *   요청까지 모두 취소로 끝낸다(헬퍼는 취소를 토스트 없는 `success: false` 로 돌려준다).
     * - 공용 토큰이 다른 탭의 세션(다른 워크스페이스 · 계정 · 로그아웃)이면 재발급하지 않고 취소로 끝낸다(30차 S30-01). 재발급은 공용
     *   리프레시 토큰으로 이 탭 워크스페이스의 토큰을 받아 공용 저장소에 쓰므로, 그대로 두면 다른 탭의 세션을 이 탭 것으로 뒤집는다.
     * - `quiet` 는 사용 중 미리 재발급(`utils/session.ts`, 30차 S30-02)이다. 실패해도 로그인으로 보내지 않는다(`getNewTokenQuietly`) —
     *   아직 액세스 토큰이 살아 있는 조용한 연장이라, 진짜 만료는 다음 요청이 지금처럼 처리한다. 진행 중인 재발급이 있으면 그것을 함께 기다린다.
     */
    async function reIssueAccessToken(
      options: { quiet?: boolean } = {},
    ): Promise<void> {
      assertTabSession();
      reissueInFlight ??= (async () => {
        const workspaceIdBefore = workspace.value.id;
        const generationBefore = sessionGeneration;
        const newToken = options.quiet
          ? await getNewTokenQuietly()
          : await getNewToken();
        // 재발급이 로그아웃 · 세션 만료보다 늦게 끝났으면 저장하지 않는다(세션을 되살리지 않는다, 27차 R27-05).
        if (newToken && generationBefore === sessionGeneration) {
          // 재발급을 기다리는 사이 다른 탭이 공용 토큰을 바꿨으면(워크스페이스 전환 등) 저장하지 않고 취소로 끝낸다 — 저장하면 그 전환을
          // 이 탭 워크스페이스로 되돌린다(30차 수정 라운드 L2).
          assertTabSession();
          saveTokens(newToken);
        }
        if (workspace.value.id && workspace.value.id !== workspaceIdBefore) {
          throw new CanceledError("Workspace switched during token renewal");
        }
      })().finally(() => {
        reissueInFlight = null;
      });
      return reissueInFlight;
    }

    /**
     * 새 토큰을 저장한다. 자동 재발급 · `must-renew` 갱신은 지금 워크스페이스를 유지한 채 토큰만
     * 새로 받는다 — 이때 리프레시 토큰이 발급 시점 최신 DB 역할을 담고 있으므로, 디코딩한
     * `workspaceId` 가 지금 `workspace` 와 같으면 `syncRole` 로 `workspace.role` · `workspaces[]` 의
     * 같은 항목을 함께 맞춘다(3차 교차 연계 보완 Z3-01 — 올라간 역할은 화면을 다시 불러올 때까지 보류, 4차 M2).
     * 이 한 곳으로 맞추면 자동 재발급 · `must-renew` ·
     * 전환(`applyWorkspaceSwitch` 가 `setWorkspace` 를 먼저 부른다, 4차 W3-03) · `applyMe` 의 조용한 재발급이
     * 모두 같은 값을 본다.
     * 워크스페이스가 다르면(전환 중 재발급 등) 손대지 않는다 — 호출부가 새 워크스페이스를 안다.
     */
    function saveTokens(newTokens: JwtTokens): void {
      try {
        tokenStorage.setTokens(newTokens.accessToken, newTokens.refreshToken);
        tokens.value = { ...newTokens };
        const decoded = jwtDecode<TokenClaims>(newTokens.accessToken);
        info.value = {
          id: decoded.id,
          loginId: decoded.loginId,
          name: decoded.name,
          type: decoded.type,
          managerFlag: decoded.managerFlag,
          authorities: decoded.authorities ?? [],
          workspaceId: decoded.workspaceId ?? null,
          role: decoded.role ?? null,
        };
        if (decoded.workspaceId === workspace.value.id && decoded.role) {
          syncRole(decoded.role);
        }
        setSentryUser(info.value);
      } catch (error: unknown) {
        logger.error("Failed to decode JWT token:", error);
        void signOut();
      }
    }

    /**
     * 다른 탭이 돌린 공용 토큰을 이 탭 스토어에 받아들인다(90차 AU90-03 — `utils/session.ts` 의 `syncWithSharedTokens` 가 부른다).
     * 저장소는 이미 그 토큰이므로 다시 쓰지 않는다(`saveTokens` 와 다른 점). 같은 워크스페이스면 `syncRole` 로 화면 역할도 맞춘다 — 다른 탭의
     * 재발급이 역할 강등을 드러냈는데 이 탭이 토큰만 받고 역할을 두면 입력칸이 열린 채 쓰기가 403 「알 수 없는 권한」 으로 끝났다.
     * 낮아진 역할은 곧바로 반영하고 올라간 역할은 `pendingRole` 로 보류한다(`syncRole` 규칙 그대로).
     * 디코딩에 실패하면 지금 값을 그대로 둔다(공용 토큰 오염은 `tabConflictOf` 가 이미 안내한다).
     */
    function adoptSharedTokens(newTokens: JwtTokens): void {
      let decoded: TokenClaims;
      try {
        decoded = jwtDecode<TokenClaims>(newTokens.accessToken);
      } catch (error: unknown) {
        logger.error("Failed to decode shared JWT token:", error);
        return;
      }
      tokens.value = { ...newTokens };
      info.value = {
        id: decoded.id,
        loginId: decoded.loginId,
        name: decoded.name,
        type: decoded.type,
        managerFlag: decoded.managerFlag,
        authorities: decoded.authorities ?? [],
        workspaceId: decoded.workspaceId ?? null,
        role: decoded.role ?? null,
      };
      if (decoded.workspaceId === workspace.value.id && decoded.role) {
        syncRole(decoded.role);
      }
      setSentryUser(info.value);
    }

    /**
     * 리프레시 토큰만 바꾼다 — 액세스 토큰 · 워크스페이스는 그대로(59차 AR59-01, 전환을 멈췄지만 서버가 이미 회전한 리프레시). 저장소와
     * 스토어를 함께 맞춰야 `loggedIn` 이 어긋나지 않는다(어긋나면 새로고침 · 새 탭이 로그인 화면으로 가며 `clearAuth` 로 모든 탭이 끊긴다).
     */
    function saveRefreshToken(refreshToken: string): void {
      tokenStorage.setRefreshToken(refreshToken);
      tokens.value = { ...tokens.value, refreshToken };
    }

    return {
      tokens,
      info,
      workspace,
      workspaces,
      pendingRole,
      loggedIn,
      authorities,
      clearAuth,
      setWorkspace,
      setWorkspaces,
      applyPendingRole,
      applyMe,
      reIssueAccessToken,
      saveTokens,
      adoptSharedTokens,
      saveRefreshToken,
    };
  },
  { persist: true },
);
